/**
 * Demo-mode sample data (shapes copied from what Caryn's real server returns; contents from the design prototype).
 * Everything lives in memory for this tab only.
 */
const now = Date.now()
const ago = (min: number) => new Date(now - min * 60000).toISOString()

export const FAN_ID = '11111111-1111-4111-8111-111111111111'
export const CREATOR_ID = '22222222-2222-4222-8222-222222222222'
export const CONV_ID = '33333333-3333-4333-8333-333333333333'

/** Sign-in sample: this email is a member; any other email is a first-time visitor (goes to "Request to join"). */
export const MEMBER_EMAIL = 'member@example.com'

export const photo = (n: 1 | 2 | 3) => `/design/gallery/feature-${n}.jpg`
export const CARYN_PHOTO = '/design/caryn-avatar.jpg'

export function demoUser(email = MEMBER_EMAIL) {
  return { id: FAN_ID, email, firstName: 'Johnny', lastName: 'Smith', first_name: 'Johnny', last_name: 'Smith', username: 'johnny', role: 'fan', avatar_url: null, phone_verified: true }
}

export const creator = { id: CREATOR_ID, username: 'Caryn', avatar_url: CARYN_PHOTO }

type Msg = Record<string, any>
let seq = 100
export const nextId = (p = 'm') => `${p}-${++seq}`

const fromCaryn = (id: string, content: string, min: number, extra: Msg = {}): Msg => ({
  id, conversation_id: CONV_ID, sender_id: CREATOR_ID, recipient_id: FAN_ID, content, attachments: [], reactions: [],
  created_at: ago(min), read_at: null, sender: creator, ...extra,
})
const fromMe = (id: string, content: string, min: number, extra: Msg = {}): Msg => ({
  id, conversation_id: CONV_ID, sender_id: FAN_ID, recipient_id: CREATOR_ID, content, attachments: [], reactions: [],
  created_at: ago(min), read_at: ago(min - 1), sender: { id: FAN_ID, username: 'johnny', avatar_url: null }, ...extra,
})

/** Oldest first. The real API returns newest first; the server reverses on the way out. */
export const messages: Msg[] = [
  fromCaryn('m1', 'Hi Johnny! I’m so happy you’re here.', 60 * 26),
  fromMe('m2', 'Hi Caryn!! Finally in 💗', 60 * 26 - 5),
  fromCaryn('m3', ' ', 60 * 5, { attachments: [{ id: 'a1', type: 'image', media_type: 'image', url: photo(2), preview_url: photo(2), is_paid: true, product_id: 'p1', price: 25, title: 'Tree House, set 2', is_purchased: false }] }),
  fromCaryn('m3v', '[Voice note]', 60 * 4.5, { attachments: [{ id: 'av1', type: 'audio', media_type: 'voice_note', url: '/design/sample-voice.m4a', mime_type: 'audio/mp4', duration: 6, title: 'Voice note' }] }),
  fromCaryn('m4', ' ', 60 * 4, { attachments: [{ id: 'a2', type: 'image', media_type: 'image', url: photo(1), preview_url: photo(1), is_freebie: true, freebie_id: 'fr1', title: 'A little gift', freebie_unlocked: false }] }),
  fromCaryn('m5', 'I thought of you when I found this one.', 40),
  fromCaryn('m6', 'Tell me what you think?', 39),
  fromMe('m7', 'That’s the one I was talking about ♡', 30, { reactions: [{ id: 'r1', user_id: CREATOR_ID, emoji: '❤️' }] }),
  fromCaryn('m8', 'I thought of you when I found this one. Tell me what you think?', 6),
]

export const accessLevel = {
  hasAccess: true, state: 'ACTIVE', source: 'stripe', tier: 'premium', expiresAt: new Date(now + 20 * 864e5).toISOString(),
  trialDaysRemaining: null, premiumDaysRemaining: 20, graceDaysRemaining: null, requiresOnboarding: false,
  requiresPaymentSetup: false, redirectTo: null, userColor: 'blue', showBanner: false, bannerMessage: null, bannerType: 'info',
  badge: null, actions: ['cancel'], warnings: [], isLegacy: false, showUpgradePrompt: false, upgradeMessage: null,
  metadata: { subscriptionId: 's1', stripeSubscriptionId: 'sub_demo', inviteId: null, entrySource: null, paymentDeferredAt: null },
  timestamp: new Date().toISOString(),
}

export const subscription = {
  id: 's1', status: 'active', plan_type: 'monthly', plan_id: 'monthly', amount: 79, cancel_at_period_end: false,
  current_period_start: ago(60 * 24 * 10), current_period_end: new Date(now + 20 * 864e5).toISOString(), created_at: ago(60 * 24 * 40),
  stripe_subscription_id: 'sub_demo',
  // the real server joins the plan (subscriptionService: plan:subscription_plans(*)); today's Manage page reads plan.name
  plan: { id: 'standard', name: 'Full Private Access', monthly_price: 79, annual_price: 700, stripe_price_id_monthly: 'price_demo_monthly', stripe_price_id_annual: 'price_demo_annual' },
}

export const albums = [
  'Texas vs. Ohio State Game', 'SPECS', 'Road Trips', 'Tree House', 'Kansas', 'Cave Creek', 'Hotel Mornings', 'Behind the Scenes', 'Golden Hour',
].map((name, i) => ({ id: `al${i}`, name, slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'), image_count: 12, sort_order: i }))

const RATIOS: [number, number][] = [[2, 3], [1, 1], [4, 5], [3, 4], [2, 3], [5, 4], [2, 3], [1, 1], [3, 4], [4, 5], [2, 3], [1, 1]]
export function galleryImages(albumIndex = -1) {
  const shift = albumIndex + 1
  return RATIOS.map(([w, h], i) => {
    const src = photo(((i + shift) % 3 + 1) as 1 | 2 | 3)
    const premium = i === 5
    return {
      id: `g${albumIndex + 1}-${i}`, album_id: albumIndex >= 0 ? albums[albumIndex].id : null, title: null, description: null,
      original_width: w * 400, original_height: h * 400, canvas_x: 0, canvas_y: 0,
      access_level: premium ? 'premium' : 'subscribers', dominant_color: '#3a3532', hasAccess: !premium, price: premium ? 15 : null,
      urls: { placeholder: null, thumbnail: src, medium: src, full: premium ? null : src, original: premium ? null : src },
      like_count: 18 + (i * 7) % 60, created_at: ago(60 * (i + 1)),
    }
  })
}
export const likedIds = new Set<string>(['g0-1', 'g0-4'])
export const galleryPurchasedIds = new Set<string>()

export const purchases = [
  { id: 'pu1', photo_id: 'p9', amount_paid: 40, status: 'succeeded', purchased_at: ago(60 * 24 * 21), photo: { id: 'p9', title: 'Tree House, set 2', media_type: 'image', preview_url: photo(1), url: photo(1), full_url: photo(1) } },
  { id: 'pu2', photo_id: 'p8', amount_paid: 25, status: 'succeeded', purchased_at: ago(60 * 24 * 31), photo: { id: 'p8', title: 'Hotel morning', media_type: 'image', preview_url: photo(3), url: photo(3), full_url: photo(3) } },
  { id: 'pu3', photo_id: 'p7', amount_paid: 30, status: 'succeeded', purchased_at: ago(60 * 24 * 57), photo: { id: 'p7', title: 'Cave Creek', media_type: 'image', preview_url: photo(2), url: photo(2), full_url: photo(2) } },
]
export const freebieUnlocks: any[] = []

/** Inbox sample mail (the design's MAILS). In live mode the Inbox reads Caryn's newsroom posts instead. */
// (Email: the demo server answers like Caryn's server - src/mock/server.ts "Email + Diary")

export const paymentMethods = [
  { id: 'card1', stripe_payment_method_id: 'pm_demo', brand: 'visa', last4: '4242', exp_month: 8, exp_year: 2028, is_default: true, created_at: ago(60 * 24 * 40) },
]
export const wallet = { balance: 15, transactions: [{ id: 'wt1', amount: 15, type: 'credit', description: 'Gift card', created_at: ago(60 * 24 * 12) }] }
export const preferences = { notifications_email: true, notifications_sms: false }


// documents signed in this demo session (Settings → Account → Documents)
export const signedDocs: { type: string; version: string; signedAt: string }[] = []
